import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthHeader } from '@/hooks/useAuth';
import { apiErrorMessage } from '@/lib/api/client';
import { getWarehouseTree } from '@/lib/api/warehouse';
import { getProductGroupTypes } from '@/lib/api/product-group-type';
import { getProductGroupTreesExtended } from '@/lib/api/product-group-tree';
import {
  getLatestStoreSkuSettingValuesDefaults,
  previewStoreSkuSettingValuesRowCount,
  startStoreSkuSettingValuesExtraction,
} from '@/lib/api/analysis-data-extraction';
import type { ProductGroupTreeExtended, ProductGroupType, WarehouseTreeNode } from '@/lib/types';
import type {
  ProductGroupDepthSelection,
  SettingPresenceFilter,
  StockFilterMatch,
  StoreSkuSettingValuesDefaults,
} from '@/lib/types/feature/analysis-data-extraction';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { WarehouseQuickSelect } from './WarehouseQuickSelect';
import { WarehouseMultiSelect } from './WarehouseMultiSelect';
import { PreviewRowCountButton } from './PreviewRowCountButton';
import { ProductGroupTreeMultiSelect } from './ProductGroupTreeMultiSelect';
import {
  buildProductGroupTree,
  buildSelections,
  expandSelectionsToLeafKeys,
} from '@/lib/utils/product-group-tree';

/** 絞り込みの選択肢。ラベルは対象の設定値ごとに変える */
const PRESENCE_OPTIONS: { value: SettingPresenceFilter; label: string }[] = [
  { value: 'all', label: 'すべて' },
  { value: 'set', label: '設定ありのみ' },
  { value: 'unset', label: '設定なしのみ' },
];
/** 最低在庫と最大在庫の結び方。両方を指定したときだけ意味を持つ */
const STOCK_MATCH_OPTIONS: { value: StockFilterMatch; label: string }[] = [
  { value: 'all', label: 'すべて満たす（かつ）' },
  { value: 'any', label: 'いずれかを満たす（または）' },
];
const DEALING_OPTIONS: { value: SettingPresenceFilter; label: string }[] = [
  { value: 'all', label: 'すべて' },
  { value: 'set', label: '展開店舗のみ' },
  { value: 'unset', label: '非展開のみ' },
];

interface StoreSkuSettingValuesFormProps {
  account: { id: string; code: string; name: string };
  onSubmitted?: () => void;
}

/**
 * 店舗×SKU 設定値リストの実行フォーム（FKV3-12855）
 *
 * 対象倉庫と対象SKU群（商品グループ値）はどちらも必須。母集団が在庫配分の全グリッドで
 * 100万行規模になるため、他メニューのような「未指定＝全件」は用意しない。
 */
export function StoreSkuSettingValuesForm({
  account,
  onSubmitted,
}: StoreSkuSettingValuesFormProps) {
  const { getAuthHeader } = useAuthHeader();

  const [warehouseNodes, setWarehouseNodes] = useState<WarehouseTreeNode[]>([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<Set<string>>(new Set());
  const warehouseIds = useMemo(
    () => warehouseNodes.filter((n) => n.node_type === 'warehouse').map((n) => n.id),
    [warehouseNodes],
  );
  // 前回実行条件から復元した倉庫が削除されている場合があるため、現存する倉庫だけに絞る
  // （検証も送信もこの値だけを見る。WeeklyStoreProductGroupForm と同じ理由）
  const effectiveWarehouseIds = useMemo(
    () => warehouseIds.filter((id) => selectedWarehouseIds.has(id)),
    [warehouseIds, selectedWarehouseIds],
  );

  const [types, setTypes] = useState<ProductGroupType[]>([]);
  const [trees, setTrees] = useState<ProductGroupTreeExtended[]>([]);
  const [typeId, setTypeId] = useState<string>('');
  // ツリーで選んだ末端ノードのキー（送信時に「配下が全選択の親」へ畳む）
  const [selectedLeafKeys, setSelectedLeafKeys] = useState<Set<string>>(new Set());
  // 前回実行条件の商品グループ。ツリーが組み上がってから選択状態へ展開する
  const [pendingSelections, setPendingSelections] = useState<ProductGroupDepthSelection[] | null>(
    null,
  );

  const [eventUpliftBaseDate, setEventUpliftBaseDate] = useState('');
  const [minStockFilter, setMinStockFilter] = useState<SettingPresenceFilter>('all');
  const [maxStockFilter, setMaxStockFilter] = useState<SettingPresenceFilter>('all');
  const [stockFilterMatch, setStockFilterMatch] = useState<StockFilterMatch>('all');
  const [dealingFlagFilter, setDealingFlagFilter] = useState<SettingPresenceFilter>('all');
  const [compressAsZip, setCompressAsZip] = useState(false);

  const [pendingDefaults, setPendingDefaults] = useState<
    StoreSkuSettingValuesDefaults | null | undefined
  >(undefined);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mastersLoading, setMastersLoading] = useState(() => Boolean(account.code));

  // アカウント切替時に前アカウントの入力・選択を持ち越さないようリセットする
  // （レンダー中の調整パターン。理由は WeeklyStoreProductGroupForm と同じ）
  const prevAccountCodeRef = useRef(account.code);
  if (account.code !== prevAccountCodeRef.current) {
    prevAccountCodeRef.current = account.code;
    setWarehouseNodes([]);
    setSelectedWarehouseIds(new Set());
    setTypes([]);
    setTrees([]);
    setTypeId('');
    setSelectedLeafKeys(new Set());
    setPendingSelections(null);
    setEventUpliftBaseDate('');
    setMinStockFilter('all');
    setMaxStockFilter('all');
    setStockFilterMatch('all');
    setDealingFlagFilter('all');
    setCompressAsZip(false);
    setPendingDefaults(undefined);
    setDefaultsApplied(false);
    setError(null);
    setMastersLoading(true);
  }

  // マスタ（倉庫ツリー・商品グループタイプ・商品階層）と前回実行条件の取得
  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    const authHeaders = getAuthHeader();

    const treePromise = getWarehouseTree(account.code, authHeaders)
      .then((nodes) => {
        if (!cancelled) setWarehouseNodes(nodes);
      })
      .catch(() => {
        if (!cancelled) setError('倉庫一覧の取得に失敗しました');
      });
    const typesPromise = getProductGroupTypes(account.code, authHeaders)
      .then((res) => {
        if (!cancelled) setTypes(res);
      })
      .catch(() => {
        if (!cancelled) setError('商品グループタイプの取得に失敗しました');
      });
    const groupsPromise = getProductGroupTreesExtended(account.code, authHeaders)
      .then((res) => {
        if (!cancelled) setTrees(res);
      })
      .catch(() => {
        if (!cancelled) setError('商品階層の取得に失敗しました');
      });
    const defaultsPromise = getLatestStoreSkuSettingValuesDefaults(account.code, authHeaders)
      .then((res) => {
        if (!cancelled) setPendingDefaults(res.has_previous_execution ? res.parameters : null);
      })
      .catch(() => {
        if (!cancelled) setPendingDefaults(null);
      });

    Promise.allSettled([treePromise, typesPromise, groupsPromise, defaultsPromise]).then(() => {
      if (!cancelled) setMastersLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [account.code]);

  // 前回実行条件をフォームへ反映（アカウント切替ごとに1回のみ）
  useEffect(() => {
    if (defaultsApplied || pendingDefaults === undefined) return;
    if (pendingDefaults) {
      setTypeId(pendingDefaults.product_group_type_id);
      setPendingSelections(pendingDefaults.product_group_selections ?? null);
      setSelectedWarehouseIds(new Set(pendingDefaults.target_warehouse_ids));
      setEventUpliftBaseDate(pendingDefaults.event_uplift_base_date ?? '');
      setMinStockFilter(pendingDefaults.min_stock_filter ?? 'all');
      // FKV3-13004 より前のジョブは最大在庫の指定を持たない（従来どおり AND で再現する）
      setMaxStockFilter(pendingDefaults.max_stock_filter ?? 'all');
      setStockFilterMatch(pendingDefaults.stock_filter_match ?? 'all');
      setDealingFlagFilter(pendingDefaults.dealing_flag_filter ?? 'all');
      setCompressAsZip(pendingDefaults.compress_as_zip ?? false);
    }
    setDefaultsApplied(true);
  }, [pendingDefaults, defaultsApplied]);

  // タイプ未選択なら先頭のタイプを既定にする（前回実行条件の反映後に走らせる）
  useEffect(() => {
    if (!defaultsApplied || typeId || types.length === 0) return;
    setTypeId(types[0].id);
  }, [defaultsApplied, typeId, types]);

  // 選択中のタイプの商品階層ツリー（プレオーダー平坦リスト）
  const treeNodes = useMemo(() => buildProductGroupTree(trees, typeId), [trees, typeId]);

  // 前回実行条件の商品グループは、ツリーが組み上がってから選択状態へ展開する
  // （親が指定されていれば配下の末端をすべて選択済みにする）
  useEffect(() => {
    if (!pendingSelections || treeNodes.length === 0) return;
    setSelectedLeafKeys(expandSelectionsToLeafKeys(treeNodes, pendingSelections));
    setPendingSelections(null);
  }, [pendingSelections, treeNodes]);

  // ツリーに無いノードが選ばれたままにならないよう、実際に送る値はツリーから組み立てる
  // （タイプを変えた直後や、前回実行条件の復元後にマスタが変わっていた場合）
  const effectiveSelections = useMemo(
    () => buildSelections(treeNodes, selectedLeafKeys),
    [treeNodes, selectedLeafKeys],
  );

  // 片方だけの絞り込みでは AND と OR が同じ結果になるため、両方指定したときだけ選ばせる
  const bothStockFiltersActive = minStockFilter !== 'all' && maxStockFilter !== 'all';

  const changeTypeId = (value: string) => {
    setTypeId(value);
    setSelectedLeafKeys(new Set());
    setPendingSelections(null);
  };

  const validate = (): string | null => {
    if (effectiveWarehouseIds.length === 0) return '対象倉庫を1つ以上選択してください';
    if (!typeId) return '対象SKU群の商品グループタイプを選択してください';
    if (effectiveSelections.length === 0) return '対象SKU群の商品グループを1つ以上選択してください';
    return null;
  };

  // 件数プレビューと実行で同じ条件を送る（片方だけ直して食い違うのを防ぐ）
  const buildRequest = () => ({
    target_warehouse_ids: effectiveWarehouseIds,
    product_group_type_id: typeId,
    product_group_selections: effectiveSelections,
    event_uplift_base_date: eventUpliftBaseDate || null,
    min_stock_filter: minStockFilter,
    max_stock_filter: maxStockFilter,
    stock_filter_match: stockFilterMatch,
    dealing_flag_filter: dealingFlagFilter,
    compress_as_zip: compressAsZip,
  });

  const handlePreviewRowCount = async () => {
    const validationError = validate();
    if (validationError) throw new Error(validationError);
    try {
      const res = await previewStoreSkuSettingValuesRowCount(
        account.code,
        buildRequest(),
        getAuthHeader(),
      );
      return res.row_count;
    } catch (e) {
      // 件数の確認は条件が広いと時間内に終わらない。サーバーが返す案内をそのまま見せる
      throw new Error(apiErrorMessage(e, '件数の取得に失敗しました'));
    }
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const authHeaders = getAuthHeader();
      await startStoreSkuSettingValuesExtraction(account.code, buildRequest(), authHeaders);
      toast.success('抽出ジョブを受け付けました。実行ログで進捗を確認できます');
      onSubmitted?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : '抽出ジョブの実行に失敗しました';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>抽出条件</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* 出力する値の時点。設定変更の前後を振り返る用途なので、現在値ではなく実行断面を出す */}
        <p className="text-sm text-muted-foreground">
          出力する設定値（最低在庫・最大在庫・展開店舗フラグ）は、直近の在庫配分が実際に読んだ値です。
          在庫日数も同じ時点の値なので、設定値と時点が揃います。
        </p>

        {/* 対象倉庫（必須） */}
        <div className="space-y-2">
          <Label>対象倉庫（必須。1つ以上選択してください）</Label>
          <p className="text-sm text-muted-foreground">
            設定値は倉庫単位で持つため、選んだ倉庫と、その倉庫に紐づく店舗の組み合わせを出力します。
            1つの倉庫に複数店舗が紐づく場合（EC等）は、同じ倉庫×SKUの行が店舗の数だけ出ます。
          </p>
          <WarehouseQuickSelect
            accountCode={account.code}
            selectedIds={selectedWarehouseIds}
            onChange={setSelectedWarehouseIds}
            idPrefix="setting-values-wh"
            disabled={mastersLoading}
          />
          <WarehouseMultiSelect
            nodes={warehouseNodes}
            selectedIds={selectedWarehouseIds}
            onChange={setSelectedWarehouseIds}
            idPrefix="setting-values-wh"
            loading={mastersLoading}
          />
        </div>

        {/* 対象SKU群（必須） */}
        <div className="space-y-2">
          <Label>対象SKU群（必須。商品グループで指定します）</Label>
          <p className="text-sm text-muted-foreground">
            全SKUを出すと在庫配分の全グリッド（アカウントによっては100万行規模）になるため、
            商品グループでの絞り込みを必須にしています。上位階層を選ぶと、その配下すべてが対象になります。
          </p>
          <div className="flex items-center gap-2">
            <Label htmlFor="setting-values-type">商品グループタイプ</Label>
            <Select value={typeId} onValueChange={changeTypeId} disabled={mastersLoading}>
              <SelectTrigger id="setting-values-type" className="w-56">
                <SelectValue placeholder="タイプを選択" />
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ProductGroupTreeMultiSelect
            nodes={treeNodes}
            selectedLeafKeys={selectedLeafKeys}
            onChange={setSelectedLeafKeys}
            idPrefix="setting-values-pg"
            loading={mastersLoading}
            emptyMessage="選択したタイプに商品グループがありません"
          />
        </div>

        {/* 設定値による絞り込み */}
        <div className="space-y-2">
          <Label>最低在庫</Label>
          <RadioGroup
            value={minStockFilter}
            onValueChange={(v) => setMinStockFilter(v as SettingPresenceFilter)}
            className="flex flex-wrap gap-4"
            disabled={mastersLoading}
          >
            {PRESENCE_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-center gap-1.5">
                <RadioGroupItem value={option.value} id={`setting-values-min-${option.value}`} />
                <Label htmlFor={`setting-values-min-${option.value}`} className="font-normal">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <p className="text-sm text-gray-500">
            「設定ありのみ」は最低在庫が1以上の行だけを出します
          </p>
        </div>

        <div className="space-y-2">
          <Label>最大在庫</Label>
          <RadioGroup
            value={maxStockFilter}
            onValueChange={(v) => setMaxStockFilter(v as SettingPresenceFilter)}
            className="flex flex-wrap gap-4"
            disabled={mastersLoading}
          >
            {PRESENCE_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-center gap-1.5">
                <RadioGroupItem value={option.value} id={`setting-values-max-${option.value}`} />
                <Label htmlFor={`setting-values-max-${option.value}`} className="font-normal">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          <p className="text-sm text-gray-500">
            「設定ありのみ」は上限が設定されている行（空欄＝上限なしを除く）だけを出します
          </p>
        </div>

        {/* 「最低在庫 or 最大在庫に設定あり」を表現するための結び方。片方だけなら意味を持たない */}
        <div className="space-y-2">
          <Label>最低在庫と最大在庫の条件</Label>
          <RadioGroup
            value={stockFilterMatch}
            onValueChange={(v) => setStockFilterMatch(v as StockFilterMatch)}
            className="flex flex-wrap gap-4"
            disabled={mastersLoading || !bothStockFiltersActive}
          >
            {STOCK_MATCH_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-center gap-1.5">
                <RadioGroupItem value={option.value} id={`setting-values-match-${option.value}`} />
                <Label
                  htmlFor={`setting-values-match-${option.value}`}
                  className={`font-normal ${bothStockFiltersActive ? '' : 'text-gray-400'}`}
                >
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
          {!bothStockFiltersActive && (
            <p className="text-sm text-gray-500">
              最低在庫と最大在庫の両方を絞り込むときに選べます
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label>展開店舗</Label>
          <RadioGroup
            value={dealingFlagFilter}
            onValueChange={(v) => setDealingFlagFilter(v as SettingPresenceFilter)}
            className="flex flex-wrap gap-4"
            disabled={mastersLoading}
          >
            {DEALING_OPTIONS.map((option) => (
              <div key={option.value} className="flex items-center gap-1.5">
                <RadioGroupItem
                  value={option.value}
                  id={`setting-values-dealing-${option.value}`}
                />
                <Label htmlFor={`setting-values-dealing-${option.value}`} className="font-normal">
                  {option.label}
                </Label>
              </div>
            ))}
          </RadioGroup>
        </div>

        {/* イベント係数の基準日 */}
        <div className="space-y-2">
          <Label htmlFor="setting-values-uplift-date">イベント係数の基準日</Label>
          <Input
            id="setting-values-uplift-date"
            type="date"
            value={eventUpliftBaseDate}
            onChange={(e) => setEventUpliftBaseDate(e.target.value)}
            className="w-auto"
            disabled={mastersLoading}
          />
          <p className="text-sm text-gray-500">
            この日を含むイベントの係数だけを出力します（未指定なら実行日）。
            競合する係数がある場合は最大値を採用します（掛け合わせません）。
            予測が実行されていないアカウントでは係数列が空欄になります。
          </p>
        </div>

        {/* ZIP圧縮 */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="setting-values-zip"
            checked={compressAsZip}
            onCheckedChange={(checked) => setCompressAsZip(checked === true)}
            disabled={mastersLoading}
          />
          <Label htmlFor="setting-values-zip" className="font-normal">
            ZIPに圧縮して出力する
          </Label>
        </div>

        <div className="flex items-center justify-between gap-4">
          {/* 実行前に件数を確かめられるようにする（0件のまま実行して空のCSVを受け取らないため） */}
          <PreviewRowCountButton
            onFetch={handlePreviewRowCount}
            disabled={mastersLoading || submitting}
            conditionKey={JSON.stringify(buildRequest())}
          />
          <Button onClick={handleSubmit} disabled={submitting || mastersLoading}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            抽出を実行
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
